import { Box, List } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Canceler } from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { SortableContainer as sortableContainer, SortableElement as sortableElement } from 'react-sortable-hoc';
import api from '../../services/api/base';
import { searchSeasonsByTitle, searchStoriesByTitle } from '../../services/api/Search';
import { getSeasonById } from '../../services/api/Season';
import { getStoryById } from '../../services/api/Story';
import { ISeason, IStory } from '../../services/types';
import SearchTextField from '../SearchTextField';
import StoryItem, { IStoryItemBaseProps } from '../StoryItem';

const useStyles = makeStyles((theme: Theme) => ({
  storyListWrap: {
    overflow: 'auto',
    height: 'auto',
  },

  storySearchWrap: {
    paddingTop: 15,
    paddingLeft: 15,
    paddingRight: 15,
    maxWidth: 350,
  },
}));

type IStoryListProps = IStoryItemBaseProps & {
  filter?: 'story' | 'season';
  stories?: IStory[];
  onSortEnd?: (newIndex: number, oldIndex: number) => void;

  height?: string | number;
  fullWidth?: boolean;
  hideSearch?: boolean;
};

const SortableBox = sortableContainer((props: any) => {
  const { children } = props;
  return <List>{children}</List>;
});

const SortableStoryItem = sortableElement(StoryItem);

const StoryList = (props: IStoryListProps): JSX.Element => {
  const classes = useStyles();

  const [stories, setStories] = useState<IStory[]>(props.stories || []);
  const [isSearching, setIsSearching] = useState<boolean>();

  const storyCanceler = useRef<Canceler>(() => undefined);
  const seasonCanceler = useRef<Canceler>(() => undefined);

  const fetchStory = async (title: string) => {
    storyCanceler.current();
    seasonCanceler.current();

    let stories: IStory[] = [];

    try {
      const searchResults: { id: number; type: 'story' | 'season'; story: IStory }[] = [];

      if (/^\d+$/.test(title)) {
        await getStoryById(Number(title))
          .then(result => {
            if (result.data.deletedAt === null) {
              searchResults.push({ id: result.data.id, type: 'story', story: result.data });
            }
          })
          .catch(e => console.log);
        await getSeasonById(Number(title))
          .then(result => {
            if (result.data.deletedAt === null) {
              searchResults.push({ id: result.data.id, type: 'season', story: { ...result.data.story, seasons: [result.data] } });
            }
          })
          .catch(e => console.log);

        stories = searchResults
          .filter(item => item.story && (props.filter === undefined || props.filter === item.type))
          .map(item => {
            return Object.assign({}, item.story, { seasonId: item.type === 'season' ? item.id : undefined }); // 검색결과가 story | season 이기 때문에 seasonId를 붙여서 구분하도록 함.
          });
      } else {
        const [fetchPromise, cancel] = searchStoriesByTitle(title, 100);
        storyCanceler.current = cancel;

        const { data } = await fetchPromise;
        const storySearchResults = data.items || [];

        if (props.filter !== 'season') {
          stories = stories.concat(storySearchResults);
        }

        if (props.filter !== 'story') {
          const seasonMap: { [key: number]: IStory } = {};
          storySearchResults.forEach(story =>
            story.seasons.forEach(season => (seasonMap[season.id] = Object.assign({}, story, { seasonId: season.id }))),
          );

          const [fetchPromise, cancel] = searchSeasonsByTitle(title, 100);
          seasonCanceler.current = cancel;

          const { data } = await fetchPromise;

          if (data && data.items) {
            data.items.forEach(season => {
              const { story, ...rest } = season;

              if (story.deletedAt === null && !seasonMap[rest.id]) {
                const storyWithSeason = Object.assign({}, story, { seasonId: rest.id });
                storyWithSeason.seasons = [rest as ISeason];

                seasonMap[rest.id] = storyWithSeason;
              }
            });
          }

          stories = stories.concat(Object.values(seasonMap));
        }
      }

      setStories(stories);
    } catch (e) {
      if (api.isCancel(e)) return;
    }

    setIsSearching(false);
  };

  useEffect(() => {
    if (props.stories) {
      setStories(props.stories);
    }
  }, [props.stories]);

  useEffect(() => {
    return () => {
      storyCanceler.current();
      seasonCanceler.current();
    };
  }, []);

  const onSortEnd = ({ newIndex, oldIndex }: { newIndex: number; oldIndex: number }) => {
    props.onSortEnd && props.onSortEnd(newIndex, oldIndex);
  };

  return (
    <Box className={props.className}>
      {!props.hideSearch && (
        <Box className={classes.storySearchWrap}>
          <SearchTextField
            placeholder={props.filter === 'season' ? 'Enter story / season title (or id)' : "Enter story's title / id"}
            fullWidth={props.fullWidth ? props.fullWidth : false}
            onValueSubmit={(value: string) => {
              if (value === '' || value.replace(/\s/g, '') === '') {
                alert('keyword must be longer than or equal to 1 characters.');
              } else {
                setIsSearching(true);
                fetchStory(value);
              }
            }}
            isSearching={isSearching}
            autoFocus
          />
        </Box>
      )}
      <div className={!props.sortable ? classes.storyListWrap : undefined} style={props.height ? { height: props.height } : undefined}>
        {stories && (
          <SortableBox lockAxis="y" useDragHandle={true} onSortEnd={onSortEnd}>
            {stories.map((story, idx) => (
              <SortableStoryItem
                key={`sortable-collection-story-item-${story.id}-${story.seasonId || '0'}-${story.type}-${story.id}`}
                index={idx}
                story={story}
                idx={idx}
                readonly={props.readonly}
                sortable={props.sortable}
                removable={props.removable}
                onItemClick={props.onItemClick}
                onRemoveClick={props.onRemoveClick}
                additionalInfo={props.additionalInfo}
                onStoryGenreTextChange={props.onStoryGenreTextChange}
              />
            ))}
          </SortableBox>
        )}
      </div>
    </Box>
  );
};

export default StoryList;
